/**
 * SVG canvas for the abstract floorplan editor.
 *
 * Rendering: plain SVG inside react-zoom-pan-pinch (wheel/pinch zoom; the
 * explicit Pan tool enables drag-panning so element drags never fight the
 * pan gesture). All pointer math goes through getScreenCTM().inverse(), so
 * canvas coordinates are correct at any zoom level.
 *
 * Tools:
 *  - select: click selects; drag moves stations / zones; corner handle
 *    resizes the selected zone. One drag = one undo step (begin_gesture).
 *  - place-station: click stamps a station of the armed type.
 *  - draw-zone: drag draws a rectangle of the armed type.
 *  - draw-flow: click a source station/zone, then a target.
 */
import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Box } from '@chakra-ui/react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import {
  FLOW_TYPE_OPTIONS,
  GRID_SIZE,
  STATION_RADIUS,
  stationTypeOption,
  zoneTypeOption,
} from './constants';
import { FlowGlyph, StationGlyph, ZoneGlyph } from './glyphs';
import type { FlowGeometry } from './glyphs';
import type { DraftAction } from './useFloorplanDraft';
import { nextTmpCid } from './useFloorplanDraft';
import type {
  DraftFlow,
  DraftStation,
  DraftZone,
  EditorTool,
  FloorplanDraft,
  FlowEndpoint,
  Selection,
} from './types';

const snap = (value: number) => Math.round(value / GRID_SIZE) * GRID_SIZE;
const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

interface Point {
  x: number;
  y: number;
}

interface EditorCanvasProps {
  draft: FloorplanDraft;
  tool: EditorTool;
  selection: Selection | null;
  canvasWidth: number;
  canvasHeight: number;
  diagramUrl: string;
  flowSource: FlowEndpoint | null;
  onSelect: (selection: Selection | null) => void;
  onFlowSource: (endpoint: FlowEndpoint | null) => void;
  dispatch: (action: DraftAction) => void;
}

type DragState =
  | { kind: 'station'; cid: string; offsetX: number; offsetY: number }
  | { kind: 'zone-move'; cid: string; offsetX: number; offsetY: number }
  | { kind: 'zone-resize'; cid: string }
  | { kind: 'zone-draw'; originX: number; originY: number };

function entityCenter(
  draft: FloorplanDraft,
  endpoint: FlowEndpoint,
): Point | null {
  if (endpoint.kind === 'station') {
    const station = draft.stations.find((s) => s.cid === endpoint.cid);
    return station ? { x: station.canvas_x, y: station.canvas_y } : null;
  }
  const zone = draft.zones.find((z) => z.cid === endpoint.cid);
  return zone
    ? {
        x: zone.canvas_x + zone.canvas_width / 2,
        y: zone.canvas_y + zone.canvas_height / 2,
      }
    : null;
}

/** Walk `from` toward `to`, stopping at the boundary of the from-entity. */
function trimEndpoint(
  draft: FloorplanDraft,
  endpoint: FlowEndpoint,
  from: Point,
  to: Point,
): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  if (endpoint.kind === 'station') {
    const trim = STATION_RADIUS + 4;
    return {
      x: from.x + (dx / length) * trim,
      y: from.y + (dy / length) * trim,
    };
  }
  const zone = draft.zones.find((z) => z.cid === endpoint.cid);
  if (!zone) return from;
  // Scale the half-vector until it exits the rect.
  const halfW = zone.canvas_width / 2;
  const halfH = zone.canvas_height / 2;
  const scale = Math.min(
    dx !== 0 ? Math.abs(halfW / dx) : Infinity,
    dy !== 0 ? Math.abs(halfH / dy) : Infinity,
  );
  const factor = Math.min(1, scale) * 0.95;
  return { x: from.x + dx * factor, y: from.y + dy * factor };
}

function flowGeometry(
  draft: FloorplanDraft,
  flow: DraftFlow,
): FlowGeometry | null {
  const fromCenter = entityCenter(draft, flow.from);
  const toCenter = entityCenter(draft, flow.to);
  if (!fromCenter || !toCenter) return null;
  const start = trimEndpoint(draft, flow.from, fromCenter, toCenter);
  const end = trimEndpoint(draft, flow.to, toCenter, fromCenter);
  return { x1: start.x, y1: start.y, x2: end.x, y2: end.y };
}

function defaultStation(stationType: string, point: Point): DraftStation {
  const option = stationTypeOption(stationType);
  return {
    cid: nextTmpCid(),
    publicId: null,
    name: option.label,
    description: '',
    station_type: stationType,
    canvas_x: point.x,
    canvas_y: point.y,
    min_staff: 1,
    recommended_staff: 2,
    staff_ratio_per_students: null,
    capacity: 0,
    requires_equipment: [],
    is_required: true,
    sort_order: 0,
  };
}

function defaultZone(
  zoneType: string,
  x: number,
  y: number,
  width: number,
  height: number,
): DraftZone {
  const option = zoneTypeOption(zoneType);
  return {
    cid: nextTmpCid(),
    publicId: null,
    name: option.label,
    description: '',
    zone_type: zoneType,
    canvas_x: x,
    canvas_y: y,
    canvas_width: width,
    canvas_height: height,
    capacity: 0,
    color: option.defaultColor,
  };
}

export function EditorCanvas({
  draft,
  tool,
  selection,
  canvasWidth,
  canvasHeight,
  diagramUrl,
  flowSource,
  onSelect,
  onFlowSource,
  dispatch,
}: EditorCanvasProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [drawRect, setDrawRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const toCanvasPoint = useCallback(
    (e: { clientX: number; clientY: number }): Point => {
      const svg = svgRef.current;
      if (!svg) return { x: 0, y: 0 };
      const ctm = svg.getScreenCTM();
      if (!ctm) return { x: 0, y: 0 };
      const point = new DOMPoint(e.clientX, e.clientY).matrixTransform(
        ctm.inverse(),
      );
      return { x: point.x, y: point.y };
    },
    [],
  );

  const handleEntityClick = useCallback(
    (endpoint: FlowEndpoint, e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      if (tool.kind === 'draw-flow') {
        if (!flowSource) {
          onFlowSource(endpoint);
          return;
        }
        if (
          flowSource.kind === endpoint.kind &&
          flowSource.cid === endpoint.cid
        ) {
          onFlowSource(null);
          return;
        }
        dispatch({
          type: 'add_flow',
          flow: {
            cid: nextTmpCid(),
            publicId: null,
            flow_type: tool.flowType,
            from: flowSource,
            to: endpoint,
            description: '',
            is_primary_path: true,
            sequence_order: draft.flows.length,
          },
        });
        onFlowSource(null);
        return;
      }
      onSelect({ kind: endpoint.kind, cid: endpoint.cid });
    },
    [tool, flowSource, draft.flows.length, dispatch, onFlowSource, onSelect],
  );

  const beginEntityDrag = useCallback(
    (endpoint: FlowEndpoint, e: ReactPointerEvent<SVGGElement>) => {
      if (tool.kind !== 'select') return;
      e.stopPropagation();
      const point = toCanvasPoint(e);
      if (endpoint.kind === 'station') {
        const station = draft.stations.find((s) => s.cid === endpoint.cid);
        if (!station) return;
        dragRef.current = {
          kind: 'station',
          cid: endpoint.cid,
          offsetX: point.x - station.canvas_x,
          offsetY: point.y - station.canvas_y,
        };
      } else {
        const zone = draft.zones.find((z) => z.cid === endpoint.cid);
        if (!zone) return;
        dragRef.current = {
          kind: 'zone-move',
          cid: endpoint.cid,
          offsetX: point.x - zone.canvas_x,
          offsetY: point.y - zone.canvas_y,
        };
      }
      dispatch({ type: 'begin_gesture' });
      onSelect({ kind: endpoint.kind, cid: endpoint.cid });
      (e.target as Element).setPointerCapture?.(e.pointerId);
    },
    [tool.kind, draft.stations, draft.zones, toCanvasPoint, dispatch, onSelect],
  );

  const beginZoneResize = useCallback(
    (cid: string, e: ReactPointerEvent<SVGRectElement>) => {
      if (tool.kind !== 'select') return;
      e.stopPropagation();
      dragRef.current = { kind: 'zone-resize', cid };
      dispatch({ type: 'begin_gesture' });
      (e.target as Element).setPointerCapture?.(e.pointerId);
    },
    [tool.kind, dispatch],
  );

  const handleCanvasPointerDown = useCallback(
    (e: ReactPointerEvent<SVGSVGElement>) => {
      if (e.button !== 0) return;
      const point = toCanvasPoint(e);
      if (tool.kind === 'place-station') {
        dispatch({
          type: 'add_station',
          station: defaultStation(tool.stationType, {
            x: clamp(snap(point.x), 0, canvasWidth),
            y: clamp(snap(point.y), 0, canvasHeight),
          }),
        });
        return;
      }
      if (tool.kind === 'draw-zone') {
        dragRef.current = {
          kind: 'zone-draw',
          originX: snap(point.x),
          originY: snap(point.y),
        };
        (e.target as Element).setPointerCapture?.(e.pointerId);
        return;
      }
      if (tool.kind === 'select') {
        onSelect(null);
      }
      if (tool.kind === 'draw-flow') {
        onFlowSource(null);
      }
    },
    [
      tool,
      toCanvasPoint,
      dispatch,
      canvasWidth,
      canvasHeight,
      onSelect,
      onFlowSource,
    ],
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<SVGSVGElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      const point = toCanvasPoint(e);
      if (drag.kind === 'station') {
        dispatch({
          type: 'preview_station_move',
          cid: drag.cid,
          x: clamp(snap(point.x - drag.offsetX), 0, canvasWidth),
          y: clamp(snap(point.y - drag.offsetY), 0, canvasHeight),
        });
      } else if (drag.kind === 'zone-move') {
        const zone = draft.zones.find((z) => z.cid === drag.cid);
        if (!zone) return;
        dispatch({
          type: 'preview_zone_rect',
          cid: drag.cid,
          x: clamp(
            snap(point.x - drag.offsetX),
            0,
            canvasWidth - zone.canvas_width,
          ),
          y: clamp(
            snap(point.y - drag.offsetY),
            0,
            canvasHeight - zone.canvas_height,
          ),
          width: zone.canvas_width,
          height: zone.canvas_height,
        });
      } else if (drag.kind === 'zone-resize') {
        const zone = draft.zones.find((z) => z.cid === drag.cid);
        if (!zone) return;
        dispatch({
          type: 'preview_zone_rect',
          cid: drag.cid,
          x: zone.canvas_x,
          y: zone.canvas_y,
          width: Math.max(GRID_SIZE * 2, snap(point.x - zone.canvas_x)),
          height: Math.max(GRID_SIZE * 2, snap(point.y - zone.canvas_y)),
        });
      } else if (drag.kind === 'zone-draw') {
        setDrawRect({
          x: Math.min(drag.originX, snap(point.x)),
          y: Math.min(drag.originY, snap(point.y)),
          width: Math.abs(snap(point.x) - drag.originX),
          height: Math.abs(snap(point.y) - drag.originY),
        });
      }
    },
    [toCanvasPoint, dispatch, draft.zones, canvasWidth, canvasHeight],
  );

  const handlePointerUp = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag?.kind === 'zone-draw' && drawRect) {
      setDrawRect(null);
      if (
        drawRect.width >= GRID_SIZE &&
        drawRect.height >= GRID_SIZE &&
        tool.kind === 'draw-zone'
      ) {
        const zone = defaultZone(
          tool.zoneType,
          drawRect.x,
          drawRect.y,
          drawRect.width,
          drawRect.height,
        );
        dispatch({ type: 'add_zone', zone });
        onSelect({ kind: 'zone', cid: zone.cid });
      }
    }
  }, [drawRect, tool, dispatch, onSelect]);

  const cursor =
    tool.kind === 'pan'
      ? 'grab'
      : tool.kind === 'select'
        ? 'default'
        : 'crosshair';

  return (
    <Box
      flex="1"
      minH={0}
      overflow="hidden"
      bg="bg.subtle"
      data-e2e="floorplan-editor-canvas"
    >
      <TransformWrapper
        minScale={0.25}
        maxScale={4}
        limitToBounds={false}
        centerOnInit
        panning={{ disabled: tool.kind !== 'pan' }}
        doubleClick={{ disabled: true }}
      >
        <TransformComponent
          wrapperStyle={{ width: '100%', height: '100%' }}
          contentStyle={{ width: '100%', height: '100%' }}
        >
          <svg
            ref={svgRef}
            width={canvasWidth}
            height={canvasHeight}
            viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
            style={{ cursor, touchAction: 'none' }}
            onPointerDown={handleCanvasPointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            role="application"
            aria-label="Floorplan canvas"
          >
            <defs>
              <pattern
                id="floorplan-grid"
                width={GRID_SIZE}
                height={GRID_SIZE}
                patternUnits="userSpaceOnUse"
              >
                <path
                  d={`M ${GRID_SIZE} 0 L 0 0 0 ${GRID_SIZE}`}
                  fill="none"
                  stroke="#CBD5E0"
                  strokeWidth={0.5}
                />
              </pattern>
              {FLOW_TYPE_OPTIONS.map((option) => (
                <marker
                  key={option.value}
                  id={`flow-arrow-${option.value}`}
                  viewBox="0 0 10 10"
                  refX={9}
                  refY={5}
                  markerWidth={7}
                  markerHeight={7}
                  orient="auto-start-reverse"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={option.color} />
                </marker>
              ))}
              <marker
                id="flow-arrow-selected"
                viewBox="0 0 10 10"
                refX={9}
                refY={5}
                markerWidth={7}
                markerHeight={7}
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#3182CE" />
              </marker>
            </defs>

            <rect
              width={canvasWidth}
              height={canvasHeight}
              fill="white"
              stroke="#A0AEC0"
            />
            {diagramUrl && (
              <image
                href={diagramUrl}
                width={canvasWidth}
                height={canvasHeight}
                opacity={0.5}
                preserveAspectRatio="xMidYMid meet"
              />
            )}
            <rect
              width={canvasWidth}
              height={canvasHeight}
              fill="url(#floorplan-grid)"
              pointerEvents="none"
            />

            {draft.zones.map((zone) => (
              <ZoneGlyph
                key={zone.cid}
                zone={zone}
                selected={
                  selection?.kind === 'zone' && selection.cid === zone.cid
                }
                flowSource={
                  flowSource?.kind === 'zone' && flowSource.cid === zone.cid
                }
                onPointerDown={(e) =>
                  beginEntityDrag({ kind: 'zone', cid: zone.cid }, e)
                }
                onClick={(e) =>
                  handleEntityClick({ kind: 'zone', cid: zone.cid }, e)
                }
                onResizePointerDown={(e) => beginZoneResize(zone.cid, e)}
              />
            ))}

            {draft.flows.map((flow) => {
              const geometry = flowGeometry(draft, flow);
              if (!geometry) return null;
              const selected =
                selection?.kind === 'flow' && selection.cid === flow.cid;
              return (
                <FlowGlyph
                  key={flow.cid}
                  flow={flow}
                  geometry={geometry}
                  selected={selected}
                  markerId={
                    selected
                      ? 'flow-arrow-selected'
                      : `flow-arrow-${flow.flow_type}`
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect({ kind: 'flow', cid: flow.cid });
                  }}
                />
              );
            })}

            {draft.stations.map((station) => (
              <StationGlyph
                key={station.cid}
                station={station}
                selected={
                  selection?.kind === 'station' && selection.cid === station.cid
                }
                flowSource={
                  flowSource?.kind === 'station' &&
                  flowSource.cid === station.cid
                }
                onPointerDown={(e) =>
                  beginEntityDrag({ kind: 'station', cid: station.cid }, e)
                }
                onClick={(e) =>
                  handleEntityClick({ kind: 'station', cid: station.cid }, e)
                }
              />
            ))}

            {drawRect && (
              <rect
                x={drawRect.x}
                y={drawRect.y}
                width={drawRect.width}
                height={drawRect.height}
                fill="#3182CE"
                fillOpacity={0.15}
                stroke="#3182CE"
                strokeDasharray="6 4"
                pointerEvents="none"
              />
            )}
          </svg>
        </TransformComponent>
      </TransformWrapper>
    </Box>
  );
}
